import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, UserCog, Mail, Phone, Shield, Save, Bell, BellOff, KeyRound, Trash2, UserX, UserCheck, Smartphone, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const ROLES = ['Administrator', 'Owner', 'Office Manager', 'Employee'];

const ROLE_CAPABILITIES = {
  Administrator: {
    description: 'Full system access for portal configuration and management',
    capabilities: [
      'Manage employees, roles, and system settings',
      'Full access to all projects, contracts, and accounting',
      'View all invoices and edit Draft/Review/Approved invoices',
      'Submit invoices for review (Draft to Review)',
      'Send approved invoices to clients',
      'Delete and cancel invoices at any status',
      'Record payments on Sent and Partial invoices',
      'Access system logs and activity history',
      'Cannot approve invoices (Owner only)',
      'Cannot revert invoices to Draft (Owner only)',
    ],
  },
  Owner: {
    description: 'Business owner with full access and approval authority',
    capabilities: [
      'All Administrator capabilities, plus:',
      'Submit invoices for review (Draft to Review)',
      'Approve invoices (Draft or Review to Approved)',
      'Revert Approved or Review invoices back to Draft',
      'Send approved invoices to clients',
      'Receives in-app notification when invoices are submitted for review',
      'Receives email notification when invoices are approved',
    ],
  },
  'Office Manager': {
    description: 'Day-to-day operations and invoice workflow management',
    capabilities: [
      'Create and edit invoices, line items, and reimbursements',
      'Submit invoices for review (Draft to Review)',
      'Send approved invoices to clients',
      'Receives email notification when invoices are approved',
      'Manage projects, contracts, proposals, and leads',
      'Process payroll and manage timesheets',
      'Cannot approve invoices (Owner only)',
      'Cannot revert invoices to Draft (Owner only)',
      'Fine-grained permissions configurable below',
    ],
  },
  Employee: {
    description: 'Staff member. Admins control approvals and visibility via the granular toggles below',
    capabilities: [
      'Submit and manage own timesheets',
      'Submit reimbursement requests',
      'Sees all records by default; admins can downgrade specific employees to "Assigned" or "None" per category',
      'No approval privileges by default; admins can grant individual approve_* permissions via the toggles',
    ],
  },
};

function RoleCapabilitiesPreview({ role }) {
  const info = ROLE_CAPABILITIES[role];
  if (!info) return null;
  return (
    <div className="rounded-md border border-border bg-secondary/20 p-3" data-testid="role-capabilities-preview">
      <p className="text-xs text-muted-foreground mb-1.5">{info.description}</p>
      <ul className="space-y-0.5">
        {info.capabilities.map((c, i) => (
          <li key={i} className={`text-xs flex items-start gap-1.5 ${c.startsWith('Cannot') ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
            <span className="mt-0.5 shrink-0">{c.startsWith('Cannot') ? '-' : '+'}</span>
            <span>{c}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
const roleBadgeColor = {
  Administrator: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  Owner: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  'Office Manager': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Employee: 'bg-primary/10 text-primary border-primary/20',
};

export default function EmployeesPage() {
  const { user, startPermPreview } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [perms, setPerms] = useState({});
  const [createMode, setCreateMode] = useState(false);
  const [form, setForm] = useState({});
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);

  const isAdmin = user?.role === 'Administrator';

  useEffect(() => {
    if (isAdmin) {
      api.get('/users').then(r => setUsers(r.data.filter(u => u.role !== 'Client')));
      api.get('/auth/settings/require-2fa').then(r => setRequire2FA(r.data.require_2fa)).catch(() => {});
    }
  }, [isAdmin]);

  // Guard: only real Administrators can access this page
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="employees-access-denied">
        <div className="text-center space-y-2">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto" />
          <h2 className="text-lg font-semibold">Access Restricted</h2>
          <p className="text-sm text-muted-foreground">Only Administrators can manage employees.</p>
        </div>
      </div>
    );
  }

  const fetchUsers = () => api.get('/users').then(r => setUsers(r.data.filter(u => u.role !== 'Client')));

  const openEdit = async (u) => {
    setSelectedUser(u);
    setCreateMode(false);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      hourly_rate: u.hourly_rate ?? 0,
      billable_rate: u.billable_rate ?? 0,
      payroll_type: u.payroll_type || 'hourly',
      fixed_pay_period_amount: u.fixed_pay_period_amount ?? 0,
      role: u.role || 'Employee',
      email_notifications_enabled: u.email_notifications_enabled !== false,
    });
    if (u.role === 'Office Manager' || u.role === 'Employee') {
      const r = await api.get(`/users/${u.id}/permissions`).catch(() => ({ data: {} }));
      setPerms(r.data || {});
    } else {
      setPerms({});
    }
    setSheetOpen(true);
  };

  const openCreate = () => {
    setCreateMode(true);
    setSelectedUser(null);
    setForm({ name: '', email: '', password: 'password123', role: 'Employee', phone: '', hourly_rate: 0, billable_rate: 0 });
    setSheetOpen(true);
  };

  const handleCreate = async () => {
    try {
      if (!form.name || !form.email) return toast.error('Name and email required');
      await api.post('/auth/register', form);
      toast.success('Employee created');
      setSheetOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.name !== selectedUser.name) payload.name = editForm.name;
      if (editForm.email !== selectedUser.email) payload.email = editForm.email;
      if (editForm.phone !== (selectedUser.phone || '')) payload.phone = editForm.phone;
      if (editForm.hourly_rate !== (selectedUser.hourly_rate ?? 0)) payload.hourly_rate = parseFloat(editForm.hourly_rate) || 0;
      if (editForm.billable_rate !== (selectedUser.billable_rate ?? 0)) payload.billable_rate = parseFloat(editForm.billable_rate) || 0;
      if (editForm.payroll_type !== (selectedUser.payroll_type || 'hourly')) payload.payroll_type = editForm.payroll_type;
      if (parseFloat(editForm.fixed_pay_period_amount || 0) !== (selectedUser.fixed_pay_period_amount ?? 0)) payload.fixed_pay_period_amount = parseFloat(editForm.fixed_pay_period_amount) || 0;
      if (editForm.role !== selectedUser.role) payload.role = editForm.role;
      const prevNotif = selectedUser.email_notifications_enabled !== false;
      if (editForm.email_notifications_enabled !== prevNotif) payload.email_notifications_enabled = editForm.email_notifications_enabled;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }
      const res = await api.put(`/users/${selectedUser.id}`, payload);
      toast.success('Employee updated');
      setSelectedUser(res.data);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!resetPw || resetPw.length < 6) return toast.error('Password must be at least 6 characters');
    try {
      const res = await api.put(`/users/${selectedUser.id}/reset-password`, { new_password: resetPw });
      toast.success(res.data.status || 'Password reset');
      setResetPw('');
      setResetPwOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };

  const handleToggleActive = async () => {
    if (!selectedUser) return;
    if (selectedUser.id === user?.id) return toast.error("Cannot deactivate your own account");
    setToggling(true);
    try {
      const endpoint = selectedUser.is_active !== false ? 'deactivate' : 'reactivate';
      await api.put(`/users/${selectedUser.id}/${endpoint}`);
      const newStatus = endpoint === 'deactivate' ? false : true;
      toast.success(`Employee ${newStatus ? 'reactivated' : 'deactivated'}`);
      setSelectedUser({ ...selectedUser, is_active: newStatus });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setToggling(false);
  };

  const handleDeleteEmployee = async () => {
    if (!selectedUser) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success(`${selectedUser.name} has been deleted`);
      setDeleteConfirmOpen(false);
      setSheetOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    setDeleting(false);
  };

  const updatePerm = async (key, value) => {
    const updated = { ...perms, [key]: value };
    setPerms(updated);
    await api.put(`/users/${selectedUser.id}/permissions`, updated);
    toast.success('Permission updated');
  };

  const filtered = users.filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()));

  const permToggles = [
    { key: 'approve_proposals', label: 'Approve Proposals' },
    { key: 'approve_timesheets', label: 'Approve Timesheets' },
    { key: 'approve_invoices', label: 'Approve Invoices' },
    { key: 'approve_reimbursements', label: 'Approve Reimbursements' },
    { key: 'view_accounting', label: 'View Accounting' },
    { key: 'manage_clients', label: 'Manage Clients' },
    { key: 'manage_employees', label: 'Manage Employees' },
    { key: 'view_status_log_global', label: 'View Status Updates (all records)' },
    { key: 'manage_digital_marketing', label: 'Manage Digital Marketing' },
    { key: 'export_projects_csv', label: 'Export Projects CSV' },
  ];

  const visibilityFields = [
    { key: 'projects_visibility', label: 'Projects' },
    { key: 'clients_visibility', label: 'Clients' },
    { key: 'invoices_visibility', label: 'Invoices' },
    { key: 'proposals_visibility', label: 'Proposals' },
  ];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Employees<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{users.length}</span></h1>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground" data-testid="create-employee-button">
          <Plus className="h-4 w-4 mr-1" /> New Employee
        </Button>
      </div>

      {/* Mandatory 2FA Toggle */}
      <div className="flex items-center justify-between border border-border rounded-md p-3 bg-card" data-testid="require-2fa-card">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Shield className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Mandatory Two-Factor Authentication</p>
            <p className="text-xs text-muted-foreground">
              {require2FA
                ? 'All employees must set up 2FA before accessing the portal'
                : 'Employees can optionally enable 2FA from their profile'}
            </p>
          </div>
        </div>
        <Switch
          checked={require2FA}
          disabled={toggling2FA}
          onCheckedChange={async (val) => {
            setToggling2FA(true);
            try {
              await api.put('/auth/settings/require-2fa', { value: val });
              setRequire2FA(val);
              toast.success(val ? 'Mandatory 2FA enabled. Employees without 2FA will be prompted on next login' : 'Mandatory 2FA disabled');
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Failed to update setting');
            }
            setToggling2FA(false);
          }}
          data-testid="require-2fa-toggle"
        />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search employees..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="employee-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employees-grid">
        {filtered.map(u => (
          <Card key={u.id} className="border-border bg-card shadow-none hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => openEdit(u)} data-testid={`employee-card-${u.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                  {u.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{u.name}</p>
                    <Badge className={`text-xs shrink-0 ${roleBadgeColor[u.role] || ''}`}>{u.role}</Badge>
                  </div>
                  <div className="mt-1.5 space-y-0.5">
                    {u.email && <p className="flex items-center gap-1 text-xs text-muted-foreground truncate"><Mail className="h-3 w-3 shrink-0" /> {u.email}</p>}
                    {u.phone && <p className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3 shrink-0" /> {u.phone}</p>}
                    {u.hourly_rate > 0 && <p className="text-xs text-primary font-medium">${u.hourly_rate}/hr</p>}
                    {u.billable_rate > 0 && <p className="text-xs text-emerald-400 font-medium">${u.billable_rate}/hr billable</p>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {!u.is_active && <Badge variant="outline" className="text-xs text-destructive border-destructive/20">Inactive</Badge>}
                    {u.totp_enabled && (
                      <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-500/20 gap-0.5">
                        <Smartphone className="h-2.5 w-2.5" /> 2FA
                      </Badge>
                    )}
                    {u.email_notifications_enabled === false && (
                      <Badge variant="outline" className="text-xs text-muted-foreground border-border gap-0.5">
                        <BellOff className="h-2.5 w-2.5" /> Emails Off
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {createMode ? (
            <>
              <SheetHeader><SheetTitle className="font-heading">New Employee</SheetTitle><SheetDescription>Create a new staff account</SheetDescription></SheetHeader>
              <div className="space-y-3 mt-4">
                <div><Label className="text-xs text-muted-foreground">Full Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="employee-form-name" /></div>
                <div><Label className="text-xs text-muted-foreground">Email *</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" data-testid="employee-form-email" /></div>
                <div><Label className="text-xs text-muted-foreground">Password</Label><Input value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-background border-border" data-testid="employee-form-password" /></div>
                <div>
                  <Label className="text-xs text-muted-foreground">Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="employee-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <RoleCapabilitiesPreview role={form.role} />
                <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" data-testid="employee-form-phone" /></div>
                <div><Label className="text-xs text-muted-foreground">Hourly Rate ($)</Label><Input type="number" step="0.01" min="0" value={form.hourly_rate || ''} onChange={e => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} className="bg-background border-border" placeholder="0.00" data-testid="employee-form-rate" /></div>
                <div><Label className="text-xs text-muted-foreground">Billable Rate ($)</Label><Input type="number" step="0.01" min="0" value={form.billable_rate || ''} onChange={e => setForm({ ...form, billable_rate: parseFloat(e.target.value) || 0 })} className="bg-background border-border" placeholder="0.00" data-testid="employee-form-billable-rate" /></div>
                <Button onClick={handleCreate} className="w-full bg-primary text-primary-foreground" data-testid="employee-form-submit">Create Employee</Button>
              </div>
            </>
          ) : selectedUser && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading flex items-center gap-2"><UserCog className="h-5 w-5 text-primary" /> Edit Employee</SheetTitle>
                <SheetDescription>Update {selectedUser.name}&apos;s record</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                {/* Editable fields */}
                <div>
                  <Label className="text-xs text-muted-foreground">Full Name</Label>
                  <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="bg-background border-border" data-testid="edit-employee-name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="bg-background border-border" data-testid="edit-employee-email" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <Input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="bg-background border-border" data-testid="edit-employee-phone" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Hourly Rate ($)</Label>
                  <Input type="number" step="0.01" min="0" value={editForm.hourly_rate} onChange={e => setEditForm({ ...editForm, hourly_rate: parseFloat(e.target.value) || 0 })} className="bg-background border-border" placeholder="0.00" data-testid="edit-employee-rate" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Billable Rate ($)</Label>
                  <Input type="number" step="0.01" min="0" value={editForm.billable_rate} onChange={e => setEditForm({ ...editForm, billable_rate: parseFloat(e.target.value) || 0 })} className="bg-background border-border" placeholder="0.00" data-testid="edit-employee-billable-rate" />
                </div>

                {/* Payroll type - only meaningful for Admin/Owner to configure */}
                {(user?.role === 'Administrator' || user?.role === 'Owner') && (
                  <div className="col-span-2 p-3 rounded-md border border-border bg-secondary/20 space-y-3" data-testid="payroll-type-section">
                    <div>
                      <Label className="text-xs text-muted-foreground">Payroll Type</Label>
                      <Select value={editForm.payroll_type || 'hourly'} onValueChange={v => setEditForm({ ...editForm, payroll_type: v })}>
                        <SelectTrigger className="bg-background border-border" data-testid="edit-employee-payroll-type"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="hourly">Hourly (timesheet × rate)</SelectItem>
                          <SelectItem value="fixed">Fixed per period</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Hourly: paycheck = approved billable hours × hourly rate.
                        Fixed: paycheck = the amount below, regardless of hours logged.
                      </p>
                    </div>
                    {editForm.payroll_type === 'fixed' && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Fixed Pay Per Period ($)</Label>
                        <Input type="number" step="0.01" min="0" value={editForm.fixed_pay_period_amount ?? 0} onChange={e => setEditForm({ ...editForm, fixed_pay_period_amount: parseFloat(e.target.value) || 0 })} className="bg-background border-border" placeholder="0.00" data-testid="edit-employee-fixed-amount" />
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Role</Label>
                  <Select value={editForm.role} onValueChange={v => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="edit-employee-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <RoleCapabilitiesPreview role={editForm.role} />

                {/* Email Notification Toggle */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {editForm.email_notifications_enabled ? (
                        <Bell className="h-4 w-4 text-primary" />
                      ) : (
                        <BellOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div>
                        <Label className="text-sm">Email Notifications</Label>
                        <p className="text-xs text-muted-foreground">Send portal email alerts to this user</p>
                      </div>
                    </div>
                    <Switch
                      checked={editForm.email_notifications_enabled}
                      onCheckedChange={v => setEditForm({ ...editForm, email_notifications_enabled: v })}
                      data-testid="edit-employee-email-toggle"
                    />
                  </div>
                </div>

                <Button onClick={handleSaveEdit} disabled={saving} className="w-full bg-primary text-primary-foreground gap-1.5" data-testid="edit-employee-save">
                  <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>

                {/* Reset Password */}
                <div className="border-t border-border pt-4">
                  {!resetPwOpen ? (
                    <Button variant="outline" size="sm" onClick={() => setResetPwOpen(true)} className="w-full border-border text-muted-foreground hover:text-foreground gap-1.5" data-testid="reset-password-toggle">
                      <KeyRound className="h-3.5 w-3.5" /> Reset Password
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">New Password for {selectedUser.name}</Label>
                      <Input
                        type="password"
                        value={resetPw}
                        onChange={e => setResetPw(e.target.value)}
                        placeholder="At least 6 characters"
                        className="bg-background border-border"
                        data-testid="admin-reset-password-input"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleResetPassword} className="bg-primary text-primary-foreground gap-1" data-testid="admin-reset-password-submit">
                          <KeyRound className="h-3 w-3" /> Set Password
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setResetPwOpen(false); setResetPw(''); }}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Reset 2FA */}
                {selectedUser.totp_enabled && selectedUser.id !== user?.id && (
                  <div className="border-t border-border pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-amber-500/30 text-amber-400 hover:text-amber-300 gap-1.5"
                      onClick={async () => {
                        if (!window.confirm(`Reset 2FA for ${selectedUser.name}? They will need to set it up again.`)) return;
                        try {
                          await api.post(`/auth/2fa/admin-reset/${selectedUser.id}`);
                          toast.success(`2FA reset for ${selectedUser.name}`);
                          setSelectedUser(prev => ({ ...prev, totp_enabled: false }));
                          setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, totp_enabled: false } : u));
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to reset 2FA');
                        }
                      }}
                      data-testid="reset-2fa-btn"
                    >
                      <Smartphone className="h-3.5 w-3.5" /> Reset 2FA
                    </Button>
                  </div>
                )}

                {/* Permissions (Office Manager & Employee — admins manage both via the same toggles) */}
                {(editForm.role === 'Office Manager' || editForm.role === 'Employee') && (
                  <>
                    <div className="border-t border-border pt-4">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-3"><Shield className="h-3 w-3" /> Permissions <span className="text-[10px] font-normal normal-case text-muted-foreground/70">(overrides role defaults)</span></h3>
                      <div className="space-y-3">
                        {permToggles.map(p => (
                          <div key={p.key} className="flex items-center justify-between">
                            <Label className="text-sm">{p.label}</Label>
                            <Switch checked={!!perms[p.key]} onCheckedChange={v => updatePerm(p.key, v)} data-testid={`perm-${p.key}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-border pt-4">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Visibility</h3>
                      <div className="space-y-3">
                        {visibilityFields.map(f => (
                          <div key={f.key}>
                            <Label className="text-xs text-muted-foreground">{f.label}</Label>
                            <Select value={perms[f.key] || 'assigned'} onValueChange={v => updatePerm(f.key, v)}>
                              <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="assigned">Assigned</SelectItem>
                                <SelectItem value="none">None</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Preview — apply the in-progress toggles to the admin's
                        own session so they can navigate around and confirm
                        the UI reacts correctly BEFORE saving. */}
                    <div className="border-t border-border pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 gap-1.5"
                        onClick={() => {
                          startPermPreview({ ...perms });
                          setSheetOpen(false);
                          toast.info('Preview mode active. Navigate the portal to see the proposed permission set in action.');
                        }}
                        data-testid="preview-perms-btn"
                      >
                        <Eye className="h-3.5 w-3.5" /> Preview these permissions
                      </Button>
                      <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
                        Applies the action toggles above to your own session so you can sanity-check button gating and page access. Does not save them. Visibility selectors are enforced server-side and will only take effect after you Save. Use the banner at the top of the portal to exit preview.
                      </p>
                    </div>
                  </>
                )}

                {/* Deactivate / Reactivate Toggle */}
                {selectedUser.id !== user?.id && (
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {selectedUser.is_active !== false ? (
                          <UserCheck className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <UserX className="h-4 w-4 text-destructive" />
                        )}
                        <div>
                          <Label className="text-sm">Account Status</Label>
                          <p className="text-xs text-muted-foreground">
                            {selectedUser.is_active !== false ? 'Active. User can log in' : 'Inactive. Login blocked'}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedUser.is_active !== false}
                        onCheckedChange={handleToggleActive}
                        disabled={toggling}
                        data-testid="toggle-employee-active"
                      />
                    </div>
                  </div>
                )}

                {/* Delete Employee */}
                {selectedUser.id !== user?.id && (
                  <div className="border-t border-border pt-4">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteConfirmOpen(true)}
                      className="w-full gap-1.5"
                      data-testid="delete-employee-button"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete Employee
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Employee</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <span className="font-semibold text-foreground">{selectedUser?.name}</span>? This action cannot be undone and will remove all their account data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} className="border-border" data-testid="delete-employee-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteEmployee} disabled={deleting} data-testid="delete-employee-confirm">
              <Trash2 className="h-4 w-4 mr-1" /> {deleting ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
